import React, { useState, useEffect, useRef } from 'react';
import { SlotMachineElement as SlotMachineElementType, QuizConfig } from '../types';

interface SlotMachineElementProps {
    element: SlotMachineElementType;
    config: QuizConfig;
    onWin?: () => void;
    onLose?: () => void;
    previewMode?: boolean;
}

const SlotMachineElement: React.FC<SlotMachineElementProps> = ({ element, config, onWin, onLose, previewMode }) => {
    const [spinning, setSpinning] = useState(false);
    const [attempts, setAttempts] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [resultMessage, setResultMessage] = useState<string | null>(null);
    const [reels, setReels] = useState<string[]>(['❓', '❓', '❓']);
    const [spinningReels, setSpinningReels] = useState<boolean[]>([false, false, false]);

    // Fixed symbols
    const symbols = ['🍒', '🍋', '🍇', '🍉', '⭐', '💎', '7️⃣', '🔔'];

    const maxAttempts = element.maxAttempts || 3;
    const alwaysWin = element.alwaysWin !== false; // Default true if undefined
    const primaryColor = config.colors.primary || '#000000';

    // Ref to track spinning state for the interval
    const spinningReelsRef = useRef<boolean[]>([false, false, false]);

    // Update ref when state changes
    useEffect(() => {
        spinningReelsRef.current = spinningReels;
    }, [spinningReels]);

    const spin = () => {
        if (spinning || gameOver) return;

        setSpinning(true);
        setSpinningReels([true, true, true]);
        const currentAttempt = attempts + 1;
        setAttempts(currentAttempt);

        // Determine outcome
        let isWin = false;
        if (alwaysWin) {
            if (currentAttempt === maxAttempts) {
                isWin = true;
            }
        } else {
            isWin = false;
        }

        // Select target symbols
        let targetSymbols: string[] = [];
        if (isWin) {
            const randomSymbol = symbols[Math.floor(Math.random() * symbols.length)];
            targetSymbols = [randomSymbol, randomSymbol, randomSymbol];
        } else {
            // Ensure they don't match
            const s1 = symbols[Math.floor(Math.random() * symbols.length)];
            const s2 = symbols[Math.floor(Math.random() * symbols.length)];
            let s3 = symbols[Math.floor(Math.random() * symbols.length)];

            // Make sure it's not a win by accident
            while (s1 === s2 && s2 === s3) {
                s3 = symbols[Math.floor(Math.random() * symbols.length)];
                if (symbols.length < 2) break; // Safety
            }
            targetSymbols = [s1, s2, s3];
        }

        const intervalTime = 80;

        const spinInterval = setInterval(() => {
            setReels(prevReels => {
                return prevReels.map((symbol, index) => {
                    if (spinningReelsRef.current[index]) {
                        return symbols[Math.floor(Math.random() * symbols.length)];
                    }
                    return symbol;
                });
            });
        }, intervalTime);

        const stopDelays = [1000, 2000, 3000];

        stopDelays.forEach((delay, index) => {
            setTimeout(() => {
                setSpinningReels(prev => {
                    const newState = [...prev];
                    newState[index] = false;
                    return newState;
                });

                setReels(prev => {
                    const newReels = [...prev];
                    newReels[index] = targetSymbols[index];
                    return newReels;
                });

                if (index === 2) {
                    clearInterval(spinInterval);
                    setSpinning(false);

                    if (isWin) {
                        setGameOver(true);
                        setResultMessage(element.winningMessage);
                        if (onWin) onWin();
                    } else if (currentAttempt >= maxAttempts) {
                        setGameOver(true);
                        setResultMessage(element.losingMessage || 'Que pena, você não ganhou.');
                        if (onLose) onLose();
                    }
                }
            }, delay);
        });
    };

    return (
        <div className="flex flex-col items-center justify-center w-full mb-6">
            <h2 className="text-xl font-bold mb-4 text-center" style={{ color: config.colors.title }}>{element.title}</h2>

            <div
                className="bg-slate-200 p-6 rounded-xl shadow-2xl relative max-w-xs mx-auto border-2 border-slate-300"
            >
                {/* Machine Body */}
                <div className="flex gap-2 mb-6 bg-white p-4 rounded-lg border-inner shadow-inner justify-center">
                    {reels.map((symbol, idx) => (
                        <div key={idx} className="w-16 h-24 flex items-center justify-center bg-slate-100 border-2 border-slate-300 rounded text-4xl overflow-hidden relative shadow-md">
                            <div className={`transition-transform duration-100 ${spinningReels[idx] ? 'animate-spin-vertical' : ''}`}>
                                {symbol}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Credits Display */}
                <div className="mb-3 flex justify-center">
                    <div className="bg-slate-800 text-yellow-400 font-mono px-4 py-1 rounded border border-slate-600 shadow-inner text-sm tracking-wider">
                        CRÉDITOS: {maxAttempts - attempts}
                    </div>
                </div>

                <div className="text-center relative z-10">
                    <button
                        onClick={spin}
                        disabled={spinning || gameOver}
                        className="w-full px-6 py-3 rounded-lg font-bold text-white shadow-lg transform transition hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 disabled:transform-none"
                        style={{
                            backgroundColor: primaryColor,
                            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                        }}
                    >
                        {spinning ? 'GIRANDO...' : (gameOver ? 'FIM DE JOGO' : 'GIRAR')}
                    </button>
                </div>
            </div>

            {/* Result Box */}
            {gameOver && (
                <div className="mt-6 text-center animate-bounce bg-white p-4 rounded-xl shadow-lg border border-slate-100 max-w-sm mx-auto w-full">
                    <h3 className={`text-lg font-bold mb-1 ${resultMessage === element.winningMessage ? 'text-green-600' : 'text-red-500'}`}>
                        {resultMessage}
                    </h3>
                    {resultMessage === element.winningMessage && element.subMessage && (
                        <p className="text-sm text-slate-600">{element.subMessage}</p>
                    )}
                </div>
            )}
            <style>{`
                @keyframes spin-vertical {
                    0% { transform: translateY(-5px); }
                    50% { transform: translateY(5px); }
                    100% { transform: translateY(-5px); }
                }
                .animate-spin-vertical {
                    animation: spin-vertical 0.1s linear infinite;
                }
            `}</style>
        </div>
    );
};

export default SlotMachineElement;
